import { useState, useEffect } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

// Reusable "type DELETE to confirm" permanent-deletion dialog — the platform
// standard guard for any irreversible removal across ATF WebWorks.
export function TypeDeleteDialog({ open, count = 1, label = "record", onConfirm, onCancel }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setText(""); setBusy(false); } }, [open]);
  if (!open) return null;
  const ready = text.trim().toUpperCase() === "DELETE";
  const run = async () => {
    if (!ready) return;
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="type-delete-dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={busy ? undefined : onCancel} />
      <div className="relative bg-navy border border-red-500/40 max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center text-red-300 shrink-0"><AlertTriangle size={18} /></span>
          <h3 className="font-heading text-xl text-white">Permanent deletion</h3>
        </div>
        <p className="text-gray-300 text-sm mb-1">This will permanently remove <span className="text-white font-semibold">{count} {label}{count === 1 ? "" : "s"}</span>.</p>
        <p className="text-red-300 text-sm mb-4">This action cannot be undone.</p>
        <label className="text-xs uppercase tracking-wide text-gray-500 mb-1.5 block">Type <span className="text-red-300 font-semibold">DELETE</span> to confirm</label>
        <input autoFocus value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
          data-testid="type-delete-input" placeholder="DELETE"
          className="w-full bg-navy-800 border border-white/20 text-white p-2.5 text-sm focus:border-red-400 focus:outline-none mb-5" />
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} disabled={busy} data-testid="type-delete-cancel" className="px-4 py-2 text-sm border border-white/20 text-gray-300 hover:bg-white/5 transition-colors">Cancel</button>
          <button onClick={run} disabled={!ready || busy} data-testid="type-delete-confirm"
            className="px-4 py-2 text-sm font-semibold flex items-center gap-2 bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {busy && <Loader2 className="animate-spin" size={14} />} Delete permanently
          </button>
        </div>
      </div>
    </div>
  );
}

export default TypeDeleteDialog;
